import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { match } from 'ts-pattern';

import { ProjectService, SurveyService } from '@ps/survey-data-access';
import {
  parseSubscriberEmails,
  ProjectUpdatePayloadInput,
  SURVEY_CLASSIFICATION_KIND,
  StandardSurveyPayloadInput,
  Survey,
  TrackerUpdateChildInput,
} from '@ps/survey-utils';

/**
 * A standard survey — and a Traditional MC
 * locale, which is one with its own id — is written a row at a time, while a
 * Group MC or Tracker writes every child together against the project.
 */
export type SurveyUpdateRequest =
  | {
      readonly kind: typeof SURVEY_CLASSIFICATION_KIND.STANDARD;
      readonly surveyId: number;
      readonly input: StandardSurveyPayloadInput;
    }
  | {
      readonly kind: typeof SURVEY_CLASSIFICATION_KIND.GROUP_MC;
      readonly projectId: number;
      readonly input: ProjectUpdatePayloadInput;
    }
  | {
      readonly kind: typeof SURVEY_CLASSIFICATION_KIND.TRACKER;
      readonly projectId: number;
      readonly input: ProjectUpdatePayloadInput<TrackerUpdateChildInput>;
    };

/**
 * The creation flow's writes, each routed to the endpoint that owns it, so
 * `SurveyCreationSaveEffects` keeps a single write collaborator.
 */
@Injectable({ providedIn: 'root' })
export class SurveyPersistenceService {
  private readonly _surveyService = inject(SurveyService);
  private readonly _projectService = inject(ProjectService);

  public update(request: SurveyUpdateRequest): Observable<Survey> {
    return match(request)
      .with(
        { kind: SURVEY_CLASSIFICATION_KIND.STANDARD },
        ({ surveyId, input }) =>
          this._surveyService.updateStandardSurvey(surveyId, input).pipe(
            map((savedSurvey) =>
              this._withRequestedMarginMaxOff(savedSurvey, input),
            ),
            switchMap((savedSurvey) =>
              this._saveCommunication(savedSurvey, input),
            ),
          ),
      )
      .with(
        { kind: SURVEY_CLASSIFICATION_KIND.GROUP_MC },
        ({ projectId, input }) =>
          this._projectService.updateMcGroupedProject(projectId, input),
      )
      .with(
        { kind: SURVEY_CLASSIFICATION_KIND.TRACKER },
        ({ projectId, input }) =>
          this._projectService.updateTrackerProject(projectId, input),
      )
      .exhaustive();
  }

  public clone(
    surveyId: number,
    isServiceOperatorCreation: boolean,
  ): Observable<Survey> {
    return this._surveyService.cloneStandardSurvey(
      surveyId,
      isServiceOperatorCreation,
    );
  }

  private _withRequestedMarginMaxOff(
    savedSurvey: Survey,
    { launch }: StandardSurveyPayloadInput,
  ): Survey {
    return launch && !launch.launchFeatures.marginMaxEnabled
      ? { ...savedSurvey, isMarginMaxEnabled: false }
      : savedSurvey;
  }

  private _saveCommunication(
    savedSurvey: Survey,
    { launch }: StandardSurveyPayloadInput,
  ): Observable<Survey> {
    if (!launch) {
      return of(savedSurvey);
    }
    const { communication } = launch;
    return this._surveyService
      .saveProjectManagers(savedSurvey, {
        buyer: communication.buyerProjectManager,
        operator: communication.operatorProjectManager,
      })
      .pipe(
        switchMap((survey) =>
          this._surveyService.saveSubscribers(
            survey,
            parseSubscriberEmails(communication.subscriberEmails),
          ),
        ),
      );
  }
}
